import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ShieldCheck, X } from "lucide-react";
import api from "../../api";
import { setUser } from "../../store/slices/authSlice";

const SESSION_KEY = "twofa_nudge_seen";
const STAFF_ROLES = ["agent", "admin", "super_admin", "pa", "dev"];

/**
 * TwoFactorNudge is a one-per-session pop-up shown after login to encourage staff
 * who don't yet have 2FA on to switch it on. Mounted in MainLayout so it can
 * appear over any page once the user lands in the app.
 *
 * Only shows when the account explicitly reports 2FA is off and the user hasn't
 * chosen "Don't remind me again" (persisted server-side). "Maybe later" only
 * dismisses for the current browser session.
 */
const TwoFactorNudge = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);

  const eligible =
    !!user &&
    STAFF_ROLES.includes(user.role) &&
    user.two_factor_enabled === false &&
    user.two_factor_nudge_dismissed === false;

  useEffect(() => {
    if (!eligible) return;
    if (sessionStorage.getItem(SESSION_KEY) === "true") return;

    let cancelled = false;
    // Small delay so it doesn't collide with the "Logged in" toast / navigation,
    // then confirm against the server before showing. A stale cached `user`
    // (e.g. 2FA was enabled in another session) must not make the "turn on 2FA"
    // nudge pop up for someone who already has it on.
    const t = setTimeout(async () => {
      let data = null;
      try {
        const res = await api.get("role-management/2fa/status/");
        data = res?.data || null;
      } catch (err) {
        data = null; // fall back to the cached flags (eligible) below
      }
      if (cancelled) return;
      if (data) {
        // Heal the cached user so other 2FA-aware UI is correct too.
        if (data.enabled && user) {
          dispatch(setUser({ ...user, two_factor_enabled: true }));
        }
        if (data.enabled || data.nudge_dismissed) return;
      }
      setOpen(true);
    }, 900);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [eligible]);

  const closeForSession = () => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setOpen(false);
  };

  const handleEnable = () => {
    closeForSession();
    navigate("/settings?tab=security");
  };

  const handleNeverAgain = async () => {
    closeForSession();
    try {
      await api.post("role-management/2fa/dismiss-nudge/");
      if (user) dispatch(setUser({ ...user, two_factor_nudge_dismissed: true }));
    } catch (err) {
      console.error("Failed to dismiss 2FA nudge", err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          type="button"
          onClick={closeForSession}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-14 h-14 rounded-2xl bg-[#e8f8fa] flex items-center justify-center mb-4">
          <ShieldCheck className="w-7 h-7 text-[#55C5D0]" />
        </div>

        <h3 className="text-xl font-semibold text-gray-900">Secure your account</h3>
        <p className="text-gray-600 text-sm mt-2 leading-relaxed">
          Turn on two-factor authentication to protect your Collings account. It only
          takes a minute, we'll text a quick code to your mobile whenever you sign in,
          so a password alone can't get someone in.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleEnable}
            className="w-full py-3 rounded-xl text-white font-medium hover:opacity-90 transition"
            style={{ backgroundColor: "#55C5D0" }}
          >
            Turn on two-factor
          </button>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={closeForSession}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              Maybe later
            </button>
            <button
              type="button"
              onClick={handleNeverAgain}
              className="px-3 py-2 text-sm text-gray-400 hover:text-gray-600"
            >
              Don't remind me again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorNudge;
